/**
 * Device service for client-side API calls
 */
import apiService from './apiService';

// Cache storage object for device data
// Format: { key: { data: [...], timestamp: Date.now() } }
const deviceCache = {
  // Cache for devices by site
  devicesBySite: {},
  
  // Cache for individual devices
  devices: {},
  
  // Cache for device hierarchy
  hierarchies: {},
  
  // Cache for daily usage
  dailyUsage: {}
};

// Cache expiration time in milliseconds (5 minutes)
const CACHE_EXPIRATION = 5 * 60 * 1000;

// Helper to check if cache is valid
const isCacheValid = (cache) => {
  return cache && 
         cache.timestamp && 
         (Date.now() - cache.timestamp < CACHE_EXPIRATION);
};

const invalidateDailyUsageCacheForDevice = (deviceId) => {
  const prefix = `${deviceId}`;
  Object.keys(deviceCache.dailyUsage).forEach((k) => {
    if (k.startsWith(prefix)) {
      delete deviceCache.dailyUsage[k];
    }
  });
};

const invalidateDeviceCachesForDevice = (deviceId) => {
  invalidateDailyUsageCacheForDevice(deviceId);
  if (deviceId != null) {
    delete deviceCache.devices[String(deviceId)];
  }
  // Site/device hierarchy caches may include this device and should not remain stale
  deviceCache.devicesBySite = {};
  deviceCache.hierarchies = {};
};

const deviceService = {
  /**
   * Get all devices with pagination
   * @param {Object} params - { limit, offset }
   * @returns {Promise<Object>} - { devices, total, limit, offset }
   */
  async fetchDevices(params = {}) {
    try {
      // Build query string for parameters
      const query = [];
      if (params.limit !== undefined) query.push(`limit=${params.limit}`);
      if (params.offset !== undefined) query.push(`offset=${params.offset}`);
      if (params.siteId !== undefined) query.push(`siteId=${params.siteId}`);
      if (params.siteIds !== undefined && params.siteIds !== null && String(params.siteIds).trim() !== '') {
        query.push(`siteIds=${encodeURIComponent(String(params.siteIds))}`);
      }
      if (params.lean !== undefined) query.push(`lean=${params.lean}`);
      if (params.firstReportedYears !== undefined && params.firstReportedYears !== null && String(params.firstReportedYears).trim() !== '') {
        query.push(`firstReportedYears=${encodeURIComponent(String(params.firstReportedYears))}`);
      }
      if (params.deviceTypes !== undefined && params.deviceTypes !== null && String(params.deviceTypes).trim() !== '') {
        query.push(`deviceTypes=${encodeURIComponent(String(params.deviceTypes))}`);
      }
      if (params.isActive !== undefined && params.isActive !== null && String(params.isActive).trim() !== '') {
        query.push(`isActive=${encodeURIComponent(String(params.isActive))}`);
      }
      if (params.sortBy !== undefined && params.sortBy !== null && String(params.sortBy).trim() !== '') {
        query.push(`sortBy=${encodeURIComponent(String(params.sortBy))}`);
      }
      if (params.sortDir !== undefined && params.sortDir !== null && String(params.sortDir).trim() !== '') {
        query.push(`sortDir=${encodeURIComponent(String(params.sortDir))}`);
      }
      
      const queryString = query.length ? `?${query.join('&')}` : '';
      return await apiService.get(`/devices${queryString}`);
    } catch (error) {
      console.error('Get devices error:', error);
      throw error;
    }
  },

  /**
   * Get device by ID
   * @param {string|number} id - Device ID
   * @returns {Promise<Object>} - Device data
   */
  async fetchDevice(id) {
    try {
      return await apiService.get(`/devices/${id}`);
    } catch (error) {
      console.error(`Get device ${id} error:`, error);
      throw error;
    }
  },

  /**
   * Create new device
   * @param {Object} deviceData - Device data
   * @returns {Promise<Object>} - Created device
   */
  async createDevice(deviceData) {
    try {
      return await apiService.post('/devices', deviceData);
    } catch (error) {
      console.error('Create device error:', error);
      throw error;
    }
  },

  /**
   * Update device
   * @param {string|number} id - Device ID
   * @param {Object} deviceData - Device data
   * @returns {Promise<Object>} - Updated device
   */
  async updateDevice(id, deviceData) {
    try {
      const res = await apiService.put(`/devices/${id}`, deviceData);
      invalidateDeviceCachesForDevice(id);
      return res;
    } catch (error) {
      console.error(`Update device ${id} error:`, error);
      throw error;
    }
  },

  /**
   * Delete device
   * @param {string|number} id - Device ID
   * @returns {Promise<void>}
   */
  async deleteDevice(id) {
    try {
      const res = await apiService.delete(`/devices/${id}`);
      invalidateDeviceCachesForDevice(id);
      return res;
    } catch (error) {
      console.error(`Delete device ${id} error:`, error);
      throw error;
    }
  },

  async recalculateDeviceUsage(id) {
    try {
      const res = await apiService.post(`/devices/${id}/recalculate-usage`, {});
      invalidateDeviceCachesForDevice(id);
      return res;
    } catch (error) {
      console.error(`Recalculate device ${id} usage error:`, error);
      throw error;
    }
  },

  /**
   * Create a device invite token/link (admin/super_user)
   * @param {string} id - Device ID
   * @returns {Promise<{success:boolean, token:string, expiresAt:string, device:{id:string, serialNumber:string, description:string}}>} response
   */
  async createDeviceInvite(id) {
    try {
      return await apiService.post(`/devices/${id}/invite`, {});
    } catch (error) {
      console.error(`Create device invite ${id} error:`, error);
      throw error;
    }
  },

  /**
   * Get device readings
   * @param {string|number} id - Device ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array|Object>} - Array of readings, or { readings, total } when includeTotal is true
   */
  async fetchDeviceReadings(id, params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset !== undefined && params.offset !== null) queryParams.append('offset', params.offset);
      if (params.order) queryParams.append('order', params.order);
      if (params.includeTotal) queryParams.append('includeTotal', 'true');

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

      return await apiService.get(`/devices/${id}/readings${queryString}`);
    } catch (error) {
      console.error(`Get device ${id} readings error:`, error);
      throw error;
    }
  },

  /**
   * Get daily usage data for a device
   * @param {number|string} id - Device ID
   * @param {Object} params - Query parameters (startDate, endDate)
   * @returns {Promise<Array>} - Daily usage data
   */
  async getDeviceDailyUsage(id, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const cacheKey = `${id}${queryString}`;
      
      // Check if we have a valid cache entry
      if (isCacheValid(deviceCache.dailyUsage[cacheKey])) {
        console.log(`Using cached daily usage for device ${id}`);
        return deviceCache.dailyUsage[cacheKey].data;
      }
      
      // Not in cache or cache expired, fetch from API
      console.log(`Fetching daily usage for device ${id} from API`);
      const dailyUsage = await apiService.get(`/devices/${id}/daily-usage${queryString}`);
      
      // Store in cache
      deviceCache.dailyUsage[cacheKey] = {
        data: dailyUsage,
        timestamp: Date.now()
      };
      
      return dailyUsage;
    } catch (error) {
      console.error(`Get device ${id} daily usage error:`, error);
      throw error;
    }
  },

  /**
   * Get devices by site ID
   * @param {string|number} siteId - Site ID
   * @returns {Promise<Array>} - Array of devices for the site
   */
  async fetchDevicesBySite(siteId) {
    try {
      // Check if we have a valid cache entry
      const cacheKey = siteId.toString();
      if (isCacheValid(deviceCache.devicesBySite[cacheKey])) {
        console.log(`Using cached devices for site ${siteId}`);
        return deviceCache.devicesBySite[cacheKey].data;
      }
      
      // Not in cache or cache expired, fetch from API
      console.log(`Fetching devices for site ${siteId} from API`);
      const devices = await apiService.get(`/sites/${siteId}/devices`);
      
      // Store in cache
      deviceCache.devicesBySite[cacheKey] = {
        data: devices,
        timestamp: Date.now()
      };
      
      return devices;
    } catch (error) {
      console.error(`Get devices for site ${siteId} error:`, error);
      throw error;
    }
  },

  /**
   * Get child devices for a parent device
   * @param {string|number} parentId - Parent device ID
   * @returns {Promise<Array>} - Array of child devices
   */
  async fetchChildDevices(parentId) {
    try {
      return await apiService.get(`/devices/${parentId}/children`);
    } catch (error) {
      console.error(`Get child devices for parent ${parentId} error:`, error);
      throw error;
    }
  },

  /**
   * Get complete device hierarchy (ancestors and descendants)
   * @param {string|number} deviceId - Device ID
   * @returns {Promise<Object>} - Device hierarchy object with ancestors and descendants
   */
  async fetchDeviceHierarchy(deviceId) {
    try {
      return await apiService.get(`/devices/${deviceId}/hierarchy`);
    } catch (error) {
      console.error(`Get device hierarchy for ${deviceId} error:`, error);
      throw error;
    }
  },

  /**
   * Get bulk daily reading counts for multiple devices
   * @param {Object} params - { date: 'YYYY-MM-DD', siteId, deviceIds: Array<string> }
   * @returns {Promise<{date: string, counts: Array<{deviceId: string, date: string, readingCount: number}>}>}
   */
  async fetchDailyReadingCounts(params = {}) {
    try {
      const rawIds = Array.isArray(params.deviceIds) ? params.deviceIds : [];
      const ids = rawIds.map((id) => String(id)).filter(Boolean);

      const baseQueryParams = new URLSearchParams();
      if (params.date) baseQueryParams.append('date', params.date);
      if (params.siteId) baseQueryParams.append('siteId', params.siteId);

      // If no ids were provided, do a single call with the other filters.
      if (ids.length === 0) {
        const queryString = baseQueryParams.toString() ? `?${baseQueryParams.toString()}` : '';
        return await apiService.get(`/devices/daily-reading-counts${queryString}`);
      }

      // Batch to avoid excessively long URLs for large device sets.
      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        chunks.push(ids.slice(i, i + chunkSize));
      }

      const countsMap = new Map();
      let dateOut = params.date;

      for (const chunk of chunks) {
        const queryParams = new URLSearchParams(baseQueryParams);
        queryParams.append('deviceIds', chunk.join(','));
        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
        const resp = await apiService.get(`/devices/daily-reading-counts${queryString}`);
        if (resp?.date) dateOut = resp.date;

        (resp?.counts || []).forEach((c) => {
          if (!c || c.deviceId == null) return;
          countsMap.set(String(c.deviceId), c);
        });
      }

      return {
        date: dateOut,
        counts: Array.from(countsMap.values())
      };
    } catch (error) {
      console.error('Fetch daily reading counts error:', error);
      throw error;
    }
  },

  /**
   * Get bulk daily usage for multiple devices
   * @param {Object} params - { deviceIds: Array<string|number>, startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }
   * @returns {Promise<{startDate: string, endDate: string, devices: Array<{deviceId: number, daily: Array<{date: string, totalUsage: number, readingCount: number}>}>}>}
   */
  async getDevicesDailyUsageBulk(params = {}) {
    try {
      const requestStartMs = Date.now();
      const rawIds = Array.isArray(params.deviceIds) ? params.deviceIds : [];
      const ids = rawIds.map((id) => String(id)).filter(Boolean);
      const resolvedSkipReconciliation = params.skipReconciliation === undefined ? true : Boolean(params.skipReconciliation);
      const resolvedRealtime = Boolean(params.realtime);
      if (ids.length === 0) {
        return { startDate: params.startDate, endDate: params.endDate, devices: [] };
      }

      const usePost = params.usePost !== false;

      if (usePost) {
        const resp = await apiService.post('/devices/daily-usage-bulk', {
          deviceIds: ids,
          startDate: params.startDate,
          endDate: params.endDate,
          includeLiveToday: params.includeLiveToday,
          skipReconciliation: resolvedSkipReconciliation,
          realtime: resolvedRealtime
        });

        console.log('Bulk daily usage request timing:', {
          method: 'POST',
          requestedDeviceCount: ids.length,
          chunkCount: 1,
          maxConcurrent: 1,
          skipReconciliation: resolvedSkipReconciliation,
          realtime: resolvedRealtime,
          elapsedMs: Date.now() - requestStartMs
        });

        return {
          startDate: resp?.startDate || params.startDate,
          endDate: resp?.endDate || params.endDate,
          devices: Array.isArray(resp?.devices) ? resp.devices : []
        };
      }

      // Many proxies/browsers will fail very long GET URLs (large sites can have 500+ devices).
      // Batch the request into smaller chunks and merge results.
      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        chunks.push(ids.slice(i, i + chunkSize));
      }

      const deviceMap = new Map();
      let startDateOut = params.startDate;
      let endDateOut = params.endDate;

      const fetchChunk = async (chunk) => {
        const queryParams = new URLSearchParams();
        queryParams.append('deviceIds', chunk.join(','));
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        if (params.includeLiveToday !== undefined && params.includeLiveToday !== null) {
          queryParams.append('includeLiveToday', params.includeLiveToday ? '1' : '0');
        }
        queryParams.append('skipReconciliation', resolvedSkipReconciliation ? '1' : '0');
        if (resolvedRealtime) queryParams.append('realtime', '1');
        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
        return apiService.get(`/devices/daily-usage-bulk${queryString}`);
      };

      const maxConcurrent = 4;

      for (let i = 0; i < chunks.length; i += maxConcurrent) {
        const batch = chunks.slice(i, i + maxConcurrent);
        const batchStartMs = Date.now();
        const responses = await Promise.all(batch.map((chunk) => fetchChunk(chunk)));

        console.log('Bulk daily usage batch timing:', {
          requestedDeviceCount: ids.length,
          batchIndex: Math.floor(i / maxConcurrent) + 1,
          batchCount: Math.ceil(chunks.length / maxConcurrent),
          requestsInBatch: batch.length,
          elapsedMs: Date.now() - batchStartMs
        });

        for (const resp of responses) {
          if (resp?.startDate) startDateOut = resp.startDate;
          if (resp?.endDate) endDateOut = resp.endDate;

          for (const entry of (resp?.devices || [])) {
            if (!entry || entry.deviceId == null) continue;
            const key = String(entry.deviceId);
            // Chunks shouldn't overlap, but keep merge logic safe.
            const existing = deviceMap.get(key);
            if (!existing) {
              deviceMap.set(key, { deviceId: entry.deviceId, daily: Array.isArray(entry.daily) ? entry.daily : [] });
              continue;
            }
            const more = Array.isArray(entry.daily) ? entry.daily : [];
            if (more.length === 0) continue;
            const merged = [...(existing.daily || []), ...more];
            deviceMap.set(key, { deviceId: existing.deviceId, daily: merged });
          }
        }
      }

      console.log('Bulk daily usage request timing:', {
        method: 'GET',
        requestedDeviceCount: ids.length,
        chunkCount: chunks.length,
        maxConcurrent,
        skipReconciliation: resolvedSkipReconciliation,
        realtime: resolvedRealtime,
        elapsedMs: Date.now() - requestStartMs
      });

      return {
        startDate: startDateOut,
        endDate: endDateOut,
        devices: Array.from(deviceMap.values())
      };
    } catch (error) {
      console.error('Fetch bulk daily usage error:', error);
      throw error;
    }
  },

  /**
   * Bulk update devices (e.g., thresholds)
   * @param {Object} payload - { deviceIds: number[], dailyThresholdLiters?: number|null }
   * @returns {Promise<{updated: number}>}
   */
  async bulkUpdateDevices(payload) {
    try {
      const res = await apiService.put('/devices/bulk', payload);
      // Bulk edits can change relationships (parentId) and site associations.
      // Ensure we don't keep stale hierarchy/site caches.
      deviceCache.devicesBySite = {};
      deviceCache.hierarchies = {};
      // Individual device cache entries may be stale too.
      deviceCache.devices = {};
      return res;
    } catch (error) {
      console.error('Bulk update devices error:', error);
      throw error;
    }
  },

  async getDeviceMultiplierHistory(id) {
    try {
      return await apiService.get(`/devices/${id}/multiplier-history`);
    } catch (error) {
      console.error(`Get device ${id} multiplier history error:`, error);
      throw error;
    }
  },

  async addDeviceMultiplierHistory(id, payload) {
    try {
      const res = await apiService.post(`/devices/${id}/multiplier-history`, payload);
      invalidateDailyUsageCacheForDevice(id);
      return res;
    } catch (error) {
      console.error(`Add device ${id} multiplier history error:`, error);
      throw error;
    }
  },

  async deleteDeviceMultiplierHistory(id, historyId) {
    try {
      const res = await apiService.delete(`/devices/${id}/multiplier-history/${historyId}`);
      invalidateDailyUsageCacheForDevice(id);
      return res;
    } catch (error) {
      console.error(`Delete device ${id} multiplier history ${historyId} error:`, error);
      throw error;
    }
  }
};

export default deviceService;
